import { AxiosInstance } from "axios"
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from "react"
import { Box } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { AccountingDebtor } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { ApiQueryAutocomplete } from "../../../../core/api/components/ApiQueryAutocomplete"
import { defaultUserDetailsState, UserDetailsProps } from "./OrderOnBehalfFlow"
import { UserType } from "../../../../modules/login/loginTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    CreateOrderOnBehalfOfQuery,
    fetchOrderOnBehalfOfCompaniesForMerchant,
    fetchOrderOnBehalfOfCompaniesForServiceCenter,
    OrderOnBehalfOfCompaniesResponse,
} from "./orderOnBehalfFlowApi"
import { TranslationKey } from "../../../../modules/localization/translations"
import { useEnvironment } from "../../../../modules/environment/envHooks"
import { EnvironmentType } from "../../../../modules/environment/envTypes"

type GroupedDebtor = AccountingDebtor &
    Readonly<{
        group: string
    }>

const useStyles = makeStyles()((theme) => ({
    input: {
        width: "100%",
        backgroundColor: theme.palette.common.white,
    },
}))

type CompanySelectProps = Readonly<{
    id: number
    userType: UserType.MERCHANT | UserType.SERVICE_CENTER
    userDetails: UserDetailsProps
    setUserDetails: Dispatch<SetStateAction<UserDetailsProps>>
    labelTranslationKey?: TranslationKey
}>

export function CompanySelect({
    id,
    userType,
    userDetails,
    setUserDetails,
    labelTranslationKey = "create-order-on-behalf-of-dialog-select-company-field",
}: CompanySelectProps) {
    const { classes } = useStyles()
    const { currentEnv } = useEnvironment()

    const selector = useCallback((data: OrderOnBehalfOfCompaniesResponse) => {
        const mappedData = data.customers.flatMap((customer) => {
            if (customer.isOfficeHotel && customer.userGroups) {
                const groupDebitors: GroupedDebtor[] = customer.userGroups.map(
                    (userGroup: { id: number; name: string; isActive: boolean }) => ({
                        type: "customerGroup",
                        group: customer.name,
                        id: userGroup.id,
                        customerId: customer.id,
                        name: userGroup.name,
                        isActive: userGroup.isActive,
                        hasSales: false,
                    }),
                )
                return groupDebitors
            } else {
                const customerDebitor: GroupedDebtor = {
                    type: "customer",
                    group: customer.name,
                    id: customer.id,
                    name: customer.name,
                    isActive: customer.isActive,
                    hasSales: false,
                }
                return customerDebitor
            }
        })

        const sortedData = mappedData
            .filter((el) => el !== undefined)
            .sort((a, b) => {
                const sortedByGroup = -b.group.localeCompare(a.group)
                if (sortedByGroup === 0) {
                    return -b.name.localeCompare(a.name)
                } else {
                    return sortedByGroup
                }
            })

        return sortedData
    }, [])

    const { queryFn, queryName } = useMemo(() => {
        return {
            queryFn: async (
                { id, type }: { id: number; type: UserType },
                axios: AxiosInstance,
                signal?: AbortSignal,
            ) => {
                // FIXME: HACK: Grab from merchant instead for these service centers
                if (
                    (currentEnv.type === EnvironmentType.Local || currentEnv.type === EnvironmentType.Test) &&
                    type === UserType.SERVICE_CENTER &&
                    id === 2776
                ) {
                    const merchantId = 2
                    return fetchOrderOnBehalfOfCompaniesForMerchant({ merchantId }, axios, signal)
                }
                if (
                    (currentEnv.type === EnvironmentType.QA || currentEnv.type === EnvironmentType.Prod) &&
                    type === UserType.SERVICE_CENTER &&
                    id === 176
                ) {
                    const merchantId = 4108
                    return fetchOrderOnBehalfOfCompaniesForMerchant({ merchantId }, axios, signal)
                }
                if (type === UserType.MERCHANT) {
                    return fetchOrderOnBehalfOfCompaniesForMerchant({ merchantId: id }, axios, signal)
                }
                return fetchOrderOnBehalfOfCompaniesForServiceCenter({ serviceCenterId: id }, axios, signal)
            },
            queryName:
                userType === UserType.MERCHANT
                    ? CreateOrderOnBehalfOfQuery.COMPANIES_MERCHANT
                    : CreateOrderOnBehalfOfQuery.COMPANIES_SERVICE_CENTER,
        }
    }, [userType])

    const {
        data: result = [],
        isError,
        isLoading,
    } = useApiQuery(queryFn, {
        queryName,
        dependencies: { id, type: userType },
        select: selector,
    })
    const handleSelectCompany = (company?: GroupedDebtor) => {
        if (!company) {
            setUserDetails(defaultUserDetailsState)
            return
        }

        if (company.type === "customerGroup") {
            setUserDetails({
                companyId: company.customerId.toString(),
                companyGroupId: company.id.toString(),
                selectedUserId: undefined,
                selectedUserName: undefined,
            })
        } else if (company.type === "customer") {
            setUserDetails({
                companyId: company.id.toString(),
                companyGroupId: undefined,
                selectedUserId: undefined,
                selectedUserName: undefined,
            })
        }
    }

    useEffect(() => {
        if (result.length === 1 && !userDetails.companyId) {
            handleSelectCompany(result[0])
        }
    }, [result, userDetails.companyId])

    const selectedValue = useMemo(() => {
        if (!userDetails.companyId || result?.length === 0) return undefined

        return (
            result.find((debtor) => {
                if (debtor.type === "customerGroup" && userDetails.companyGroupId && userDetails.companyId) {
                    return debtor.customerId === +userDetails.companyId && debtor.id === +userDetails.companyGroupId
                }
                return debtor.type === "customer" && userDetails.companyId && debtor.id === +userDetails.companyId
            }) || undefined
        )
    }, [userDetails.companyId, userDetails.companyGroupId, result])

    const autocompleteKey = useMemo(() => {
        return `${userDetails.companyId || "none"}-${userDetails.companyGroupId || "none"}`
    }, [userDetails.companyId, userDetails.companyGroupId])

    return (
        <Box width="100%">
            <LocalizedStrict id={labelTranslationKey} attrs={{ label: true }}>
                <ApiQueryAutocomplete
                    key={autocompleteKey}
                    isLoading={isLoading}
                    isError={isError}
                    disableClearable
                    options={result}
                    fullWidth
                    value={selectedValue}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    groupBy={(option) => option?.group}
                    getOptionLabel={(option) => `${option.name}`}
                    renderOption={(props, option) => (
                        <Box component="li" sx={{ ml: "0.5rem" }} {...props}>
                            {option.name}
                        </Box>
                    )}
                    onChange={(e, value) => handleSelectCompany(value)}
                    className={classes.input}
                />
            </LocalizedStrict>
        </Box>
    )
}
